import type { DesignItem } from '@core/types';
import { useConfigStore } from '@designer/stores/configStore';
import { useDesignStore, type ViewName } from '@designer/stores/designStore';
import { useProductStore } from '@designer/stores/productStore';
import { resolveViewAreaSizes, resolveViewPhysicalSizesCm } from '@designer/utils/printArea';
import { imagePrintDpi, resolvePreflightProfile, resolveViewPrintContext } from './engine/preflight';

/** Value of the `image.print.dpi` filter: what Preflight's low-DPI check would see for an item. */
export interface ImagePrintDpi {
  actual: number;
  target: number;
  min: number;
}

/**
 * Print resolution of a (possibly not yet committed) image item on the active view, with the
 * same inputs as the status panel so editors such as the cropper show the number Preflight
 * will report. Null for non-images or when the source / print size is unknown.
 */
export function imageDpiForItem(item: DesignItem): ImagePrintDpi | null {
  if (!item || item.type !== 'image') return null;
  // The addon bundles its own pinia; the host instance must be passed explicitly.
  const pinia = window.Veyop?.designer?.pinia;
  if (!pinia) return null;
  const config = useConfigStore(pinia);
  const design = useDesignStore(pinia);
  const product = useProductStore(pinia);

  const stageWidth = Number(config.data.items.boxWidth) || 500;
  const stageHeight = Number(config.data.items.boxHeight) || 500;
  const options = {
    stageWidth,
    stageHeight,
    areaSizes: resolveViewAreaSizes(product.product?.areas, stageWidth, stageHeight),
    physicalSizesCm: resolveViewPhysicalSizesCm(product.product?.params),
    profile: config.data.items.preflightProfile,
  };
  const ctx = resolveViewPrintContext(design.activeView as ViewName, resolvePreflightProfile(options), options);
  const actual = imagePrintDpi(item, ctx);
  if (actual == null) return null;
  return { actual, target: ctx.targetDpi, min: ctx.minDpi };
}
